import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Shared navigation for the AML Program workspace (nested under Compliance). Built on the
// exact same pattern as AiGovernance/AiGovNav.jsx: the active tab is derived from the current
// route, so no page tracks its own selected state.
//
// Phase 1 scope only (LLD §8): an "AML Documents" tab exists in the original wireframe but has
// no page design behind it yet (LLD §9, open question) — left out here rather than wired to a
// dead route; add it once that screen is designed.
const AML_TABS = [
  { to: '/dashboard/aml', i18n: 'Aml.overview', label: 'Overview', exact: true },
  { to: '/dashboard/aml/risk-assessment', i18n: 'Aml.riskAssessment', label: 'Risk Assessment' },
  { to: '/dashboard/aml/segments', i18n: 'Aml.segments', label: 'Customer Segments' },
  { to: '/dashboard/aml/filings', i18n: 'Aml.filings', label: 'Filings' },
  { to: '/dashboard/aml/vendors', i18n: 'Aml.vendorDd', label: 'Due Diligence' },
];

// frameworks/frameworkId/onFrameworkChange come from the host page's useAmlFramework() — every
// AML table is framework-scoped, so the tab links must carry the active ?frameworkId= along when
// switching pages, and a switcher appears whenever the org matches more than one AML framework.
export default function AmlNav({ frameworks = [], frameworkId, onFrameworkChange }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation();

  const isActive = (tab) =>
    tab.exact ? pathname === tab.to || pathname === `${tab.to}/` : pathname.startsWith(tab.to);

  const go = (to) => navigate(frameworkId ? `${to}?frameworkId=${frameworkId}` : to);

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', marginBottom: frameworks.length > 1 ? '0.5rem' : 0 }}>
      <div className="aml-tabs" role="tablist" aria-label="AML Program">
        {AML_TABS.map((tab) => (
          <button
            key={tab.to}
            type="button"
            role="tab"
            aria-selected={isActive(tab)}
            className={`aml-tab ${isActive(tab) ? 'aml-tab-active' : ''}`}
            onClick={() => go(tab.to)}
          >
            {t(tab.i18n, tab.label)}
          </button>
        ))}
      </div>
      {frameworks.length > 1 && (
        <select className="aml-select" value={frameworkId || ''} onChange={(e) => onFrameworkChange(Number(e.target.value))}>
          {frameworks.map((f) => <option key={f.id} value={f.id}>{f.displayName}</option>)}
        </select>
      )}
    </div>
  );
}
